import { translations, getUILanguage, isRTL } from '../../i18n';
import type { Competition, Language } from '../../config/types';
import { getUserAvatarLink, getProfilePath } from './user-avatar';
import { dueliTruncateAttrs } from '../../lib/userTextDisplay';
import { Sanitize } from '../../lib/services/Sanitize';

export interface CompetitionCardProps extends Competition {
  subcategory_slug?: string;
  category_color?: string;
  category_icon?: string;
  creator_username?: string;
  creator_name?: string;
  creator_avatar?: string;
  opponent_username?: string;
  opponent_name?: string;
  opponent_avatar?: string;
  likes_count?: number;
  dislikes_count?: number;
  is_live?: boolean;
}

export function getCompetitionCard(item: CompetitionCardProps, lang: Language): string {
  const tr = translations[getUILanguage(lang)];
  const rtl = isRTL(lang);
  const collections: Record<string, string> = tr.watch_collections || {};
  const addLabel = collections.add || 'Save to Watch Later';
  const removeLabel = collections.remove || 'Remove from Watch Later';

  // Determine background color using subcategory priority
  // Controller logic ensures item.category_color is the correct specific color (subcategory or parent fallback)
  const hexColor = item.category_color || '#8B5CF6';

  // Fixed width to prevent overlap
  const commonClasses = "duel-thumbnail shadow-lg relative h-48 rounded-2xl overflow-hidden transition-transform hover:scale-[1.02] w-full";

  const isLive = item.status === 'live';
  const isPending = item.status === 'pending';
  const isAccepted = item.status === 'accepted';

  // Format date/time
  const date = item.scheduled_at || item.started_at || item.created_at;
  let dateDisplay = '';
  if (date) {
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      dateDisplay = d.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' });
    } else {
      // Format as DD-MM-YYYY
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      dateDisplay = `${day}-${month}-${year}`;
    }
  }

  // Generate 6 random icons for background pattern
  // Deterministic "randomness" based on ID to avoid hydration mismatches
  const idNum = item.id;
  const icons = Array(6).fill(0).map((_, i) => {
    const seed = (idNum * (i + 1) * 9301 + 49297) % 233280;
    const rng = seed / 233280;

    // Random properties
    const size = 30 + Math.floor(rng * 50); // 30-80px
    const top = Math.floor(rng * 80); // 0-80%
    const left = Math.floor(((rng * 100) + (i * 20)) % 90); // Distributed horizontal
    const rotate = Math.floor(rng * 360);
    const opacity = 0.05 + (rng * 0.1); // 0.05 - 0.15 opacity

    return `<i class="${item.category_icon || 'fas fa-trophy'}" data-csp-style="position: absolute; top: ${top}%; ${rtl ? 'right' : 'left'}: ${left}%; font-size: ${size}px; transform: rotate(${rotate}deg); opacity: ${opacity}; color: white; pointer-events: none;"></i>`;
  }).join('');

  return `
      <div class="duel-card snap-start" data-competition-id="${item.id}">
        <a href="/competition/${item.id}?lang=${lang}" class="block">
          <div class="${commonClasses}" data-csp-style="background-color: ${hexColor}">
            <!-- Background Gradient -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10 z-0"></div>
            
            <!-- Scattered Icons -->
            <div class="absolute inset-0 overflow-hidden z-0">
                ${icons}
            </div>

            <!-- Content Container -->
            <div class="absolute inset-0 flex items-center justify-center gap-3 z-10 p-4">
              <div class="flex flex-col items-center">
                <div class="competitor-avatar p-0.5 transform hover:scale-105 transition-transform duration-300">
                  ${getUserAvatarLink({
                    username: item.creator_username,
                    displayName: item.creator_name,
                    avatarUrl: item.creator_avatar,
                    fallbackSeed: item.creator_name || 'user',
                    lang,
                    nested: true,
                    className: 'block w-full h-full rounded-full cursor-pointer'
                  })}
                </div>
              </div>

              <div class="w-12 h-12 bg-white rounded-full shadow-lg z-20 flex items-center justify-center transform hover:rotate-180 transition-transform duration-500">
                <img src="/static/dueli-icon.png" alt="VS" width="48" height="48" decoding="async" fetchpriority="high" class="w-full h-full object-contain">
              </div>

              <div class="flex flex-col items-center">
                <div class="competitor-avatar p-0.5 transform hover:scale-105 transition-transform duration-300">
                  ${item.opponent_name ?
      getUserAvatarLink({
        username: item.opponent_username,
        displayName: item.opponent_name,
        avatarUrl: item.opponent_avatar,
        fallbackSeed: item.opponent_name || 'opponent',
        lang,
        nested: true,
        className: 'block w-full h-full rounded-full cursor-pointer'
      }) :
      `<div class="w-full h-full rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-2xl font-bold border-2 border-white/20 animate-pulse">?</div>`
    }
                </div>
              </div>
            </div>

            <div class="absolute top-3 ${rtl ? 'right-3' : 'left-3'} z-20">
              ${isLive ? `<span class="badge-live shadow-md"><span class="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse"></span>${tr.status_live}</span>` :
      isPending ? `<span class="badge-pending shadow-md">${tr.status_pending}</span>` :
        isAccepted ? `<span class="px-2.5 py-1 rounded-full bg-blue-500 text-white text-xs font-bold shadow-md"><i class="fas fa-check me-1"></i>${tr.status_accepted || 'Ready'}</span>` :
          `<span class="badge-recorded shadow-md"><i class="fas fa-play text-xs ${rtl ? 'recorded-play-rtl' : ''}"></i>${tr.recorded}</span>`}
            </div>
            
            <!-- Date/Time in top right corner -->
            <div class="absolute top-3 ${rtl ? 'left-3' : 'right-3'} z-20">
              <span class="px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs font-bold flex items-center gap-1.5 border border-white/10 shadow-sm">
                <i class="fas fa-clock text-[10px]"></i>
                ${dateDisplay || '--'}
              </span>
            </div>

            <!-- Views in bottom right, Likes in bottom left -->
            <div class="absolute bottom-3 ${rtl ? 'right-3' : 'left-3'} z-20">
              <span class="px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs font-medium flex items-center gap-3 border border-white/10 shadow-sm">
                <span class="flex items-center gap-1.5 transition-colors hover:text-green-400" title="${tr.like?.title || 'Likes'}">
                  <i class="fas fa-thumbs-up text-green-400"></i>
                  ${(item.likes_count || 0).toLocaleString()}
                </span>
                <span class="flex items-center gap-1.5 transition-colors hover:text-red-400" title="${tr.interactions.dislikes_count}">
                  <i class="fas fa-thumbs-down text-red-400"></i>
                  ${(item.dislikes_count || 0).toLocaleString()}
                </span>
              </span>
            </div>
            
            <div class="absolute bottom-3 ${rtl ? 'left-3' : 'right-3'} z-20">
              <span class="px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs font-medium flex items-center gap-1.5 border border-white/10 shadow-sm" title="${tr.viewers || 'Views'}">
                <i class="fas fa-eye"></i>
                ${(item.total_views || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </a>

        <div class="mt-3 px-1">
          <div class="flex items-start justify-between gap-2">
            <a href="/competition/${item.id}?lang=${lang}" class="min-w-0 flex-1">
              <h3 class="text-sm font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight hover:text-purple-600 dark:hover:text-purple-400 transition-all duration-200 min-w-0" ${dueliTruncateAttrs(item.title)}>${item.title}${item.is_fake ? ` <span data-demo-badge="1" class="inline-block align-middle text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/90 text-amber-950" title="${Sanitize.escapeHtml(tr.demo?.title || 'Demo')}">${tr.demo?.badge || 'Demo'}</span>` : ''}</h3>
            </a>
            <!-- UNIT32.1: explicit Watch Later control (shared by every card).
                 The label/state is hydrated from the authenticated API; a
                 guest click opens the existing login modal and never navigates
                 with a caller-supplied return URL. -->
            <button type="button"
              class="duel-card-watch-later shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              data-csp-on="click" data-csp-fn="WatchLaterUI.toggle" data-csp-args='[${item.id}]'
              data-csp-stop="1"
              data-watch-later-toggle="${item.id}"
              data-saved="0"
              data-wl-add-label="${addLabel}"
              data-wl-remove-label="${removeLabel}"
              data-wl-error="${collections.save_failed || 'Could not update Watch Later.'}"
              title="${addLabel}" aria-label="${addLabel}" aria-pressed="false">
              <i class="far fa-bookmark" aria-hidden="true"></i>
            </button>
          </div>
          <div class="flex items-center gap-1.5 mt-2 text-xs text-gray-500 font-medium whitespace-nowrap overflow-hidden">
            ${getProfilePath(item.creator_username, lang) ? `<a href="${getProfilePath(item.creator_username, lang)}" class="hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center gap-1" data-csp-stop="1">
               ${getUserAvatarLink({
                 username: item.creator_username,
                 displayName: item.creator_name,
                 avatarUrl: item.creator_avatar,
                 fallbackSeed: item.creator_name || 'user',
                 lang,
                 imgClassName: 'w-4 h-4 rounded-full bg-gray-200',
                 linkToProfile: false
               })}
               <span class="truncate max-w-[80px] min-w-0" ${dueliTruncateAttrs(item.creator_name || 'User')}>${item.creator_name || 'User'}</span>
            </a>` : `<span class="flex items-center gap-1">
               ${getUserAvatarLink({
                 username: item.creator_username,
                 displayName: item.creator_name,
                 avatarUrl: item.creator_avatar,
                 fallbackSeed: item.creator_name || 'user',
                 lang,
                 imgClassName: 'w-4 h-4 rounded-full bg-gray-200'
               })}
               <span class="truncate max-w-[80px] min-w-0" ${dueliTruncateAttrs(item.creator_name || 'User')}>${item.creator_name || 'User'}</span>
            </span>`}
            <span class="mx-0.5 text-gray-300 flex items-center">
                <img src="/static/dueli-icon.png" alt="" aria-hidden="true" width="20" height="20" decoding="async" class="w-5 h-5 object-contain grayscale opacity-50">
                <span class="sr-only">vs</span>
             </span>
             ${item.opponent_name ?
      (getProfilePath(item.opponent_username, lang) ? `<a href="${getProfilePath(item.opponent_username, lang)}" class="hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center gap-1" data-csp-stop="1">
                ${getUserAvatarLink({
                  username: item.opponent_username,
                  displayName: item.opponent_name,
                  avatarUrl: item.opponent_avatar,
                  fallbackSeed: item.opponent_name || 'opponent',
                  lang,
                  imgClassName: 'w-4 h-4 rounded-full bg-gray-200',
                  linkToProfile: false
                })}
                <span class="truncate max-w-[80px] min-w-0" ${dueliTruncateAttrs(item.opponent_name || '')}>${item.opponent_name}</span>
              </a>` : `<span class="flex items-center gap-1">
                ${getUserAvatarLink({
                  username: item.opponent_username,
                  displayName: item.opponent_name,
                  avatarUrl: item.opponent_avatar,
                  fallbackSeed: item.opponent_name || 'opponent',
                  lang,
                  imgClassName: 'w-4 h-4 rounded-full bg-gray-200'
                })}
                <span class="truncate max-w-[80px] min-w-0" ${dueliTruncateAttrs(item.opponent_name || '')}>${item.opponent_name}</span>
              </span>`) :
      `<span class="text-gray-400">?</span>`
    }
          </div>
        </div>
      </div>
    `;
}
